import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import { downscalePortalPhoto } from '@/shared/lib/downscaleImage';
import { resourceImageFailureText } from './resourceImageText';

/**
 * Fotos einer Ressource. Hochladen verkleinert vorher auf 2560 px wie bei den Anreise-Bildern: die
 * Gäste sehen sie am Telefon, und mehr ist dort keine Information, sondern Wartezeit.
 *
 * Ein Fehler kommt immer an: solange das Panel steht als Satz darin, nach dem Schließen des Sheets
 * als Hinweis — sonst hielte die Verwaltung ein gescheitertes Foto für hochgeladen.
 */
export function useResourceImages(groupId: string, resourceId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const queryKey = ['property-groups', groupId, 'resources', resourceId, 'images'];
  const [failure, setFailure] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const report = async (error: unknown) => {
    const text = resourceImageFailureText(await toApiError(error));
    if (mounted.current) setFailure(text);
    else toast.error(text);
  };
  const common = {
    // Offline pausierte TanStack die Aktion sonst still — der Knopf drehte ohne Satz.
    networkMode: 'always' as const,
    onMutate: () => setFailure(null),
    onError: report,
    onSettled: () => void qc.invalidateQueries({ queryKey }),
  };

  const query = useQuery({
    queryKey,
    queryFn: () => api.resources.images.list(groupId, resourceId),
  });

  const upload = useMutation({
    ...common,
    mutationFn: async (file: File) =>
      api.resources.images.upload(groupId, resourceId, await downscalePortalPhoto(file)),
  });

  const remove = useMutation({
    ...common,
    mutationFn: (imageId: string) => api.resources.images.remove(groupId, resourceId, imageId),
  });

  const move = useMutation({
    ...common,
    mutationFn: (input: { imageId: string; direction: 'Up' | 'Down' }) =>
      api.resources.images.move(groupId, resourceId, input.imageId, input.direction),
  });

  return { query, upload, remove, move, failure };
}
